// This file was created using Claude AI on 2026-01-06
import { UserText } from '@Constants/UserText';
import { getSentryTraceId } from '@utils/traceIdUtils';
import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | undefined;
    errorInfo: ErrorInfo | undefined;
    sentryEventId: string | undefined;
}

export class ReactErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: undefined,
            errorInfo: undefined,
            sentryEventId: undefined,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return {
            hasError: true,
            error,
        };
    }

    override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        const eventId = Sentry.captureReactException(error, errorInfo);

        this.setState({
            error,
            errorInfo,
            sentryEventId: eventId,
        });
    }

    handleReload = (): void => {
        window.location.reload();
    };

    handleGoHome = (): void => {
        window.location.href = '/';
    };

    handleCopyTraceId = async (traceId: string): Promise<void> => {
        try {
            await navigator.clipboard.writeText(traceId);
        } catch (err) {
            console.error('Failed to copy trace ID:', err);
        }
    };

    override render(): ReactNode {
        if (this.state.hasError && this.state.error) {
            const eventId = this.state.sentryEventId;
            const traceId = eventId || getSentryTraceId();
            const displayTraceId = traceId ? traceId : UserText.ERROR_TRACE_ID_UNAVAILABLE;

            return (
                <div className="fixed inset-0 bg-(--mantine-color-black)/50 flex items-center justify-center z-[9999] p-5 font-sans">
                    <div className="max-w-2xl w-full bg-(--mantine-color-white) rounded-lg p-6 shadow-lg">
                        <div className="flex flex-col gap-4">
                            <h1 className="m-0 text-2xl text-(--mantine-color-red-7)">
                                {UserText.ERROR_BOUNDARY_TITLE}
                            </h1>
                            <p className="m-0 text-(--mantine-color-gray-9)">{UserText.ERROR_BOUNDARY_MESSAGE}</p>

                            <div className="p-3 bg-(--mantine-color-gray-1) rounded border border-(--mantine-color-gray-4)">
                                <div className="flex items-center gap-2">
                                    <strong>{UserText.ERROR_TRACE_ID_LABEL}:</strong>
                                    <code className="flex-1">{displayTraceId}</code>
                                    {displayTraceId !== UserText.ERROR_TRACE_ID_UNAVAILABLE && (
                                        <button
                                            onClick={() => this.handleCopyTraceId(displayTraceId)}
                                            className="px-3 py-1 bg-(--mantine-color-blue-6) text-(--mantine-color-white) border-none rounded cursor-pointer text-sm hover:bg-(--mantine-color-blue-7)"
                                        >
                                            {UserText.ERROR_TRACE_ID_COPY}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={this.handleReload}
                                    className="px-5 py-2.5 bg-(--mantine-color-blue-6) text-(--mantine-color-white) border-none rounded cursor-pointer text-base font-medium hover:bg-(--mantine-color-blue-7)"
                                >
                                    {UserText.ERROR_RELOAD_BUTTON}
                                </button>
                                <button
                                    onClick={this.handleGoHome}
                                    className="px-5 py-2.5 bg-(--mantine-color-gray-7) text-(--mantine-color-white) border-none rounded cursor-pointer text-base font-medium hover:bg-(--mantine-color-gray-8)"
                                >
                                    {UserText.ERROR_HOME_BUTTON}
                                </button>
                            </div>

                            <details>
                                <summary className="cursor-pointer select-none font-medium text-(--mantine-color-blue-6)">
                                    {UserText.ERROR_TECHNICAL_DETAILS}
                                </summary>
                                <div className="mt-3 p-3 bg-(--mantine-color-gray-1) rounded border border-(--mantine-color-gray-4) max-h-96 overflow-y-auto overflow-x-auto">
                                    <pre className="m-0 text-xs whitespace-pre-wrap text-(--mantine-color-gray-9)">
                                        {this.state.error.message}
                                        {'\n\nStack Trace:\n'}
                                        {this.state.error.stack}
                                        {this.state.errorInfo?.componentStack &&
                                            `\n\nComponent Stack:${this.state.errorInfo.componentStack}`}
                                    </pre>
                                </div>
                            </details>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
